import { lastValueFrom } from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { type Injector, runInInjectionContext } from '@angular/core';
import { ActivityEntityName, EntityStatusType, TaskSystemStatusName } from '@jms/model';
import type { Task } from '@jms/model';
import { TaskStatusSetService } from '@workbuddy/entities/shared/task.status.set.service';
import { injectWorkBuddyHost } from '@workbuddy/services/host/workbuddy.host.service';
import { EntityStatusUpdateComponent, type EntityStatusUpdateContext, type EntityStatusUpdateResult } from '@workbuddy/ui/blocks/entity.status.update';
import { PageMode } from '@workbuddy/ui/blocks/page';
import { ToastService } from '@workbuddy/ui/services';
import { WorkBuddyDialogService } from '@workbuddy/ui/v2/blocks/dialog';
import { SorTaskCountService } from '../services/item.task.count.service';
import type { ContractorBulkCommand, ContractorBulkOutcome } from './contractor.item.delete.bulk.action';
import { resolveContractorBulkLines, runContractorBulkLines } from './contractor.item.bulk.tally';
import { resolveOpenSorTask } from './contractor.item.complete.bulk.action';

export const CONTRACTOR_ITEM_RESOLVE_BULK_ACTION = 'items.contractor.resolve';

/**
 * One status dialog for the selection, offering the Inactive Task statuses the contractor may pick minus Completed
 * (the SOR Details split-button list, whatever the tenant has); the chosen status, reason and note go to each line's task.
 */
export async function handleContractorItemResolveBulkCommand(args: ContractorBulkCommand, injector: Injector, itemsChanged?: () => void): Promise<ContractorBulkOutcome> {

  if (args?.action !== CONTRACTOR_ITEM_RESOLVE_BULK_ACTION) return { handled: false };

  const lines = resolveContractorBulkLines(args.data);
  if (!lines.length) return { handled: true };

  // The whole set, so Completed is found even where the subcontractor may not pick it; a failed read must not open a picker that could offer it.
  const statuses = await injector.get(TaskStatusSetService).loadAllStatuses();
  if (!statuses) {
    injector.get(ToastService).error('Mark SOR as Resolved', 'The task statuses could not be loaded. Try again.');
    return { handled: true };
  }

  const completed = statuses.find(status => status.systemName === TaskSystemStatusName.Completed);
  const context: EntityStatusUpdateContext = { title: 'Mark SOR as Resolved', entityType: ActivityEntityName.Task, displayType: EntityStatusType.Inactive, excludeIds: completed?._id ? [completed._id] : [] };
  // In the middle of the screen, like the web SORs tab's ⌘ Actions status moves.
  const ref = injector.get(WorkBuddyDialogService).create<EntityStatusUpdateResult, EntityStatusUpdateComponent>(EntityStatusUpdateComponent, {
    mode: 'modal',
    inputs: { context, sideDialog: false, layout: PageMode.Modal },
    outputs: {
      onSave: (chosen: EntityStatusUpdateResult) => ref.close(chosen),
      onCancel: () => ref.dismiss(),
    },
  });
  const chosen = await ref.result;
  if (!chosen?._id) return { handled: true };

  const http = injector.get(HttpClient);
  const taskCounts = injector.get(SorTaskCountService);
  const taskApi = runInInjectionContext(injector, injectWorkBuddyHost).taskApi;

  await runContractorBulkLines(lines, injector, async line => {
    const task = await resolveOpenSorTask(taskCounts, line);
    if ('kind' in task) return task;

    await lastValueFrom(http.patch<Task>(`${taskApi}/${task._id}/status`, { statusId: chosen._id, reasonId: chosen.reason?._id, note: chosen.note }));
    return { kind: 'updated' };
  }, itemsChanged, { noun: 'SOR(s)' });

  return { handled: true, clearSelection: true };
}
